import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Paper,
  Alert
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { downloadBlob } from '../../../../../modules/refunds/utils/documentUtils';

interface CptHelpDialogProps {
  open: boolean;
  onClose: () => void;
}

const CptHelpDialog: React.FC<CptHelpDialogProps> = ({ open, onClose }) => {
  const handleDownloadTemplate = () => {
    const headers = ['Code', 'Name', 'Category', 'ApcCode', 'BaseRate', 'GrouperPointer', 'MultiProcedureDiscount', 'RelativeWeight'];
    const csvContent = headers.join(',');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, 'cpt_template.csv');
  };

  const csvColumns = [
    { field: 'Code', description: 'CPT code (5-digit numeric code)', example: '90834' },
    { field: 'Name', description: 'CPT procedure description/name', example: 'Psychotherapy, 45 minutes' },
    { field: 'Category', description: 'CPT category classification', example: 'Medicine' },
    { field: 'ApcCode', description: 'Ambulatory Payment Classification code', example: '5821' },
    { field: 'BaseRate', description: 'Base rate for the procedure', example: '150.00' },
    { field: 'GrouperPointer', description: 'Single character grouper pointer/code', example: 'A' },
    { field: 'MultiProcedureDiscount', description: 'Whether to apply multi-procedure discount', example: 'Y' },
    { field: 'RelativeWeight', description: 'Relative weight (decimal, optional)', example: '1.5' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>CPT Management Help</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              About CPT Codes
            </Typography>
            <Typography variant="body2" paragraph>
              Current Procedural Terminology (CPT) codes are a uniform coding system that describes medical, surgical, 
              and diagnostic services. CPT codes are used to communicate with insurers about the procedures and services 
              provided to patients and are essential for billing and reimbursement processes.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              CSV Upload Format
            </Typography>
            <Typography variant="body2" paragraph>
              Upload a CSV file with the following columns. The first row should contain column headers:
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Column Name</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Example</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvColumns.map((column) => (
                    <TableRow key={column.field}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {column.field}
                      </TableCell>
                      <TableCell>{column.description}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {column.example}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Import Behavior:</strong> Uploading a CSV file will replace ALL existing CPT codes in this fee schedule.
              Make sure to include all CPT codes you want to keep in your CSV file.
            </Typography>
          </Alert>

          <Box>
            <Typography variant="h6" gutterBottom>
              Field Details
            </Typography>
            <Box sx={{ ml: 2 }}>
              <Typography variant="body2" paragraph>
                <strong>Code:</strong> Must be a 5-digit numeric code following standard CPT format (e.g., 90834, 99213).
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Name:</strong> Descriptive name of the procedure or service being coded.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Category:</strong> Classification category such as Medicine, Surgery, Radiology, etc.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>ApcCode:</strong> Ambulatory Payment Classification code used for outpatient reimbursement.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>BaseRate:</strong> Base rate amount for the procedure as a decimal number (e.g., 150.00).
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>GrouperPointer:</strong> Single character grouper pointer code used for classification purposes (e.g., A, B, C).
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>MultiProcedureDiscount:</strong> Boolean value indicating if multi-procedure discounts apply. Accepts: Y, N, Yes, No, True, False, 1, or 0.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>RelativeWeight:</strong> Relative weight value for this procedure as a decimal number (optional, can be left blank).
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              Example CSV Content
            </Typography>
            <Box sx={{ 
              bgcolor: 'grey.100', 
              p: 2, 
              borderRadius: 1, 
              fontFamily: 'monospace', 
              fontSize: '0.875rem',
              overflow: 'auto'
            }}>
              <pre>{`Code,Name,Category,ApcCode,BaseRate,GrouperPointer,MultiProcedureDiscount,RelativeWeight
90834,Psychotherapy 45 minutes,Medicine,5821,150.00,A,Y,1.5
99213,Office visit established patient,Medicine,5012,120.50,B,N,1.2
70450,CT Head without contrast,Radiology,5522,350.75,C,Y,2.0`}</pre>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
          size="small"
        >
          Download Template
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CptHelpDialog;


